/* td-reorder.js — Storefront reorder page (/pages/reorder).
 *
 * Lists the logged-in customer's past reorderable STICKER and TAPE designs and lets
 * them reorder one in a click. Tape cards (2026-10-15, briefs/tape-reorder-and-add-
 * rolls-2026-10-15.md) price from a quote table the BRIDGE computes with the same
 * functions it charges with (item.tape.quotes) — this file does no tape arithmetic. Data path is deliberately independent of the Liquid
 * `customer` object (unproven under New Customer Accounts): the page always renders
 * a shell, this fetches the App-Proxy list endpoint, and branches on the response.
 *
 *   GET  /apps/reorder-spec?action=list   (App Proxy → bridge; session cookie →
 *                                           Shopify injects logged_in_customer_id)
 *     200 { ok:true, items:[...] }         → render list (or empty state)
 *     401 { code:'NOT_LOGGED_IN' }         → soft-gate interstitial
 *     other                                → friendly error panel
 *
 * Reorder → build a REGULAR sticker spec (strip offer/sample flags) + entered qty,
 * sign { product_type, variant_id, quantity, spec } with the page nonce (identical
 * scheme to the builders), POST to the bridge, redirect to the returned invoiceUrl.
 * The server recomputes price (A1) — the client price here is display-only.
 */
(function () {
  'use strict';

  var PRODUCT_NAMES = {
    vinyl: 'Vinyl', holographic: 'Holographic', glitter: 'Glitter', chrome: 'Chrome',
    clear: 'Clear', bumper: 'Bumper', qr_code: 'QR Code', sheets: 'Sticker Sheets'
  };
  var CHECKOUT_ENDPOINT_DEFAULT = 'https://tapedudes-checkout-bridge.vercel.app/api/create-draft-checkout';

  // Gift line-icon for the bonus row — same glyph family as the drawer promo icon,
  // 18px. Static markup (no user data) so innerHTML is safe.
  var GIFT_SVG = '<svg class="tdr-card__bonus-ico" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';

  // Quantity-bump ladder — the same rungs the PDP proof modal bumps between, so a
  // customer who has seen one recognises the other. Naturally suppressed above 300
  // (no entry), which covers the brief's "suppressed at the top rung".
  var BUMP_MAP = { 50: 100, 100: 200, 200: 300 };

  function r2(n) { return Math.round(n * 100) / 100; }

  // Reorder DEAL (config.reorder_offer). Returns the resolved block, or null when the
  // offer is absent / unconfigured — graceful degradation to the plain single-price
  // layout. Everything below MUST stay identical to the bridge's reorderDealPrice() +
  // computeReorderDiscount(), or the card lies about what checkout charges (A1).
  function offerConfig(config) {
    var r = config && config.reorder_offer;
    if (!r) return null;
    var flats = r.deal_prices || {};
    var unit = Number(r.floor_unit) || 0;
    if (!Object.keys(flats).length && !(unit > 0)) return null;
    var stl = r.skip_the_line;
    return {
      dealPrices: flats,
      floorUnit: unit,
      minOffList: Number(r.min_off_list) || 0,
      bonusRate: Number(r.bonus_rate) || 0,
      suppressLadder: r.suppress_bonus_ladder === true,
      skip: (stl && stl.enabled === true && Number(stl.price) > 0)
        ? {
            price: Number(stl.price),
            bonus: Number(stl.bonus_quantity) || 0,
            sub: stl.line_sub || 'Your order jumps to the front of the print queue.',
            note: stl.line_note || ''
          }
        : null,
      appliesTo: Array.isArray(r.applies_to) ? r.applies_to : null
    };
  }
  function offerAppliesTo(offer, productType) {
    if (!offer) return false;
    if (!offer.appliesTo) return true; // no product restriction → applies to all
    return offer.appliesTo.indexOf(productType) !== -1;
  }

  // Mirror of the bridge's reorderDealPrice(). The flat rungs are the two prices
  // printed on the packing slip; every other rung derives from floor_unit (45¢).
  //
  // The min() applies at EVERY rung, INCLUDING the flat two — it is the half that
  // keeps the deal honest. Without it a flat $29.99 charges 3.5× list on a 1"×1"
  // custom size (list $8.62), and 45¢ × qty runs above list at volume (3" vinyl is
  // 43.2¢/sticker at 500). It never moves $29.99 / $44.99 on a standard size.
  function dealPrice(offer, list, qty) {
    if (!offer || !(list > 0) || !(qty > 0)) return null;
    var flat = offer.dealPrices[String(qty)];
    // Unrecognised quantity falls back to the rate, never to full price.
    var target = flat != null ? Number(flat) : (offer.floorUnit > 0 ? offer.floorUnit * qty : null);
    if (!(target > 0)) return null;
    return Math.min(target, list * (1 - offer.minOffList));
  }

  // What checkout will actually charge, derived exactly as the bridge derives it: the
  // line-item discount is round2(list) − round2(deal), so the charge lands on the deal
  // price to the cent. Returns null when the deal doesn't apply → plain list price.
  function dealCharge(offer, list, qty) {
    var d = dealPrice(offer, list, qty);
    if (d == null) return null;
    var pay = r2(d);
    var save = r2(r2(list) - pay);
    if (!(save > 0)) return null;
    return { pay: pay, save: save };
  }

  function initAll(scope) {
    var s = scope || document;
    var root = (s.matches && s.matches('[data-td-reorder]')) ? s : s.querySelector('[data-td-reorder]');
    if (root && !root.__tdrInit) { root.__tdrInit = true; initReorder(root); }
  }

  function initReorder(root) {
    var cfg = {
      configUrl: root.dataset.configUrl,
      listEndpoint: root.dataset.listEndpoint || '/apps/reorder-spec',
      checkoutEndpoint: root.dataset.checkoutEndpoint || CHECKOUT_ENDPOINT_DEFAULT,
      variantId: root.dataset.variantId,
      nonce: root.dataset.signingNonce,
      loginUrl: root.dataset.loginUrl || '/customer_authentication/login',
      bumpEnabled: root.dataset.bumpEnabled === 'true'
    };

    var el = {
      status: root.querySelector('[data-td-reorder-status]'),
      gate: root.querySelector('[data-td-reorder-gate]'),
      empty: root.querySelector('[data-td-reorder-empty]'),
      error: root.querySelector('[data-td-reorder-error]'),
      list: root.querySelector('[data-td-reorder-list]'),
      login: root.querySelector('[data-td-reorder-login]'),
      retry: root.querySelector('[data-td-reorder-retry]'),
      bump: root.querySelector('[data-td-reorder-bump]'),
      bumpTitle: root.querySelector('[data-td-reorder-bump-title]'),
      bumpBody: root.querySelector('[data-td-reorder-bump-body]'),
      bumpYes: root.querySelector('[data-td-reorder-bump-yes]'),
      bumpNo: root.querySelector('[data-td-reorder-bump-no]')
    };

    // ── Slip attribution (?s=) across the login round-trip ──────────────────────
    // The packing-slip QR carries ?s=slip-sample|slip-paid. A logged-out scanner is
    // bounced to /customer_authentication/login and returned to a param-LESS
    // /pages/reorder — we keep return_to clean + relative and do NOT smuggle the
    // query inside it (return_to is documented relative-only; betting the one-hop
    // fix on Shopify honoring an encoded query isn't worth a tracking param).
    // Instead: stash the WHITELISTED value in sessionStorage before the bounce and
    // re-attach it on return — same-origin, same-tab, so the shop.tapedudes.com hop
    // never touches tapedudes.com sessionStorage. One-shot (consumed on read) so a
    // later organic visit in the same tab can't be mis-attributed to the slip.
    var SLIP_KEY = 'tdr_slip_src';
    var SLIP_ALLOWED = { 'slip-sample': 1, 'slip-paid': 1 }; // never reflect anything else

    function readSlip(search) {
      var m = /[?&]s=([^&#]+)/.exec(search || '');
      if (!m) return null;
      var v;
      try { v = decodeURIComponent(m[1]); } catch (e) { return null; }
      return SLIP_ALLOWED[v] ? v : null;
    }
    function setSlipStash(v) { try { sessionStorage.setItem(SLIP_KEY, v); } catch (e) {} }
    function takeSlipStash() {
      var v = null;
      try { v = sessionStorage.getItem(SLIP_KEY); sessionStorage.removeItem(SLIP_KEY); } catch (e) { return null; }
      return (v && SLIP_ALLOWED[v]) ? v : null;
    }

    (function carrySlipSource() {
      var here = readSlip(window.location.search);
      if (here) { setSlipStash(here); return; }  // param present → stash for a possible login bounce
      var restored = takeSlipStash();            // param absent → restore a stashed one (post-login return)
      if (!restored || !window.history || !window.history.replaceState) return;
      var sep = window.location.search ? '&' : '?';
      var url = window.location.pathname + window.location.search + sep + 's=' + encodeURIComponent(restored) + window.location.hash;
      try { window.history.replaceState(null, '', url); } catch (e) {}
    })();

    // Carry the customer back to this page after login. `routes.account_login_url`
    // always lands on the order index by design (Shopify docs) — it never honors a
    // return param. The documented way to return to a storefront page under New
    // Customer Accounts is `/customer_authentication/login?return_to=`; `return_to`
    // accepts RELATIVE URLs only, so we keep it a clean, param-less '/pages/reorder'
    // (the ?s= slip source rides sessionStorage above, not the return_to query).
    if (el.login) {
      var ret = encodeURIComponent('/pages/reorder');
      el.login.href = cfg.loginUrl + (cfg.loginUrl.indexOf('?') === -1 ? '?' : '&') + 'return_to=' + ret;
    }
    if (el.retry) el.retry.addEventListener('click', function () { load(); });

    var config = null;

    // ── ONE MORE THING — the quantity-bump modal (§1.4) ────────────────────────
    // Page-level, not per-card: it fires ONCE per page load across every card, so a
    // customer reordering three designs is interrupted once, not three times. No
    // proof-time claim and no bonus-sticker totals in here — both muddied the PDP
    // modal (owner). Purely: more stickers, this much more money.
    var bumpShown = false;
    var bumpResolve = null;

    function closeBump(accepted) {
      if (!el.bump) return;
      el.bump.hidden = true;
      document.removeEventListener('keydown', bumpKey);
      var done = bumpResolve;
      bumpResolve = null;
      if (done) done(accepted);
    }
    function bumpKey(e) { if (e.key === 'Escape' || e.keyCode === 27) closeBump(false); }

    // Falls through to `then(false)` when the markup is missing, so a stale section that
    // predates the modal still checks out — the upsell is never allowed to block an order.
    function openBump(bump, then) {
      if (!el.bump || !el.bumpYes || !el.bumpNo) { then(false); return; }
      if (el.bumpTitle) el.bumpTitle.textContent = 'Go bigger for ' + formatMoney(bump.delta) + ' more';
      if (el.bumpBody) el.bumpBody.textContent = bump.to + ' stickers instead of ' + bump.from + '.';
      el.bumpYes.textContent = 'Yes — make it ' + bump.to;
      el.bumpNo.textContent = 'No thanks, keep ' + bump.from;
      bumpResolve = then;
      el.bump.hidden = false;
      document.addEventListener('keydown', bumpKey);
      try { el.bumpYes.focus(); } catch (e) {}
    }

    if (el.bumpYes) el.bumpYes.addEventListener('click', function () { closeBump(true); });
    if (el.bumpNo) el.bumpNo.addEventListener('click', function () { closeBump(false); });
    if (el.bump) {
      el.bump.addEventListener('click', function (e) {
        if (e.target === el.bump) closeBump(false); // backdrop = decline, order still proceeds
      });
    }

    function show(which) {
      ['status', 'gate', 'empty', 'error', 'list'].forEach(function (k) {
        if (el[k]) el[k].hidden = (k !== which);
      });
    }

    function load() {
      show('status');
      // Config + list in parallel; both must succeed to render prices.
      Promise.all([
        config ? Promise.resolve(config) : fetch(cfg.configUrl).then(function (r) {
          if (!r.ok) throw new Error('config ' + r.status); return r.json();
        }),
        fetchList()
      ]).then(function (out) {
        config = out[0];
        var result = out[1]; // { state:'ok'|'gate'|'error', items? }
        if (result.state === 'gate') { show('gate'); return; }
        if (result.state === 'error') { show('error'); return; }
        var items = (result.items || []).filter(function (it) {
          if (it && it.product_line === 'tape') return !!(it.tape && it.tape.quotes && it.tape.options && it.tape.options.length);
          return it && it.reorder_spec && config.products[it.reorder_spec.product_type];
        });
        if (!items.length) { show('empty'); return; }
        renderList(items);
        show('list');
      }).catch(function (e) {
        if (window.console) console.warn('[td-reorder] load failed', e);
        show('error');
      });
    }

    // Resolve the list request into a UI state without throwing on the expected
    // logged-out 401 (that's a gate, not an error).
    function fetchList() {
      return fetch(cfg.listEndpoint + '?action=list', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (r) {
        return r.json().then(function (data) { return { r: r, data: data }; }, function () { return { r: r, data: null }; });
      }).then(function (o) {
        if (o.r.ok && o.data && o.data.ok) return { state: 'ok', items: o.data.items || [] };
        if (o.r.status === 401 && o.data && o.data.code === 'NOT_LOGGED_IN') return { state: 'gate' };
        if (window.console) console.warn('[td-reorder] list', o.r.status, o.data && o.data.code);
        return { state: 'error' };
      }, function (e) {
        if (window.console) console.warn('[td-reorder] list fetch threw', e);
        return { state: 'error' };
      });
    }

    // §2a — re-mint the reorder token at CLICK time. Reorder tokens live 30 min; a
    // customer can read "$74.59", get distracted, and click an hour later on a dead
    // token → the bridge fails closed and charges $87.75 after showing $74.59 (the one
    // worst outcome this feature can produce). So we always fetch a FRESH token right
    // before posting, rather than trusting the one stashed at page load. One extra
    // round-trip (~300ms), hidden behind the button's "Starting your order…" state.
    // Returns { state:'ok', token } | { state:'gate' } (session expired) | { state:'error' }.
    function fetchFreshToken(orderId) {
      return fetch(cfg.listEndpoint + '?action=list', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (r) {
        return r.json().then(function (data) { return { r: r, data: data }; }, function () { return { r: r, data: null }; });
      }).then(function (o) {
        if (o.r.status === 401 && o.data && o.data.code === 'NOT_LOGGED_IN') return { state: 'gate' };
        if (!o.r.ok || !o.data || !o.data.ok) return { state: 'error' };
        var items = o.data.items || [];
        var match = null;
        for (var i = 0; i < items.length; i++) {
          if (String(items[i].order_id) === String(orderId)) { match = items[i]; break; }
        }
        if (!match || !match.reorder_token) return { state: 'error' };
        return { state: 'ok', token: match.reorder_token };
      }, function (e) {
        if (window.console) console.warn('[td-reorder] token re-fetch threw', e);
        return { state: 'error' };
      });
    }

    function renderList(items) {
      el.list.textContent = '';
      items.forEach(function (item) {
        el.list.appendChild(item.product_line === 'tape' ? renderTapeCard(item) : renderCard(item));
      });
    }

    // ── One design card ──────────────────────────────────────────────────────
    function renderCard(item) {
      var spec = item.reorder_spec;
      var product = config.products[spec.product_type];

      var card = ce('div', 'tdr-card');

      // Thumbnail (proof-or-artwork, already resolved server-side).
      var thumbWrap = ce('div', 'tdr-card__thumb');
      if (item.thumb_url) {
        var img = document.createElement('img');
        img.className = 'tdr-card__img';
        img.alt = 'Your design';
        img.loading = 'lazy';
        img.src = thumbUrl(item.thumb_url);
        img.addEventListener('error', function () {
          thumbWrap.classList.add('tdr-card__thumb--empty');
          if (img.parentNode) img.parentNode.removeChild(img);
        });
        thumbWrap.appendChild(img);
      } else {
        thumbWrap.classList.add('tdr-card__thumb--empty');
      }
      card.appendChild(thumbWrap);

      // Content: name/date, spec summary, then ONE action line — Quantity + dropdown
      // on the left, price + Reorder on the right (owner: keep them on a single row).
      var body = ce('div', 'tdr-card__body');

      var meta = ce('p', 'tdr-card__meta');
      meta.textContent = [item.order_name, formatDate(item.created_at)].filter(Boolean).join(' · ');
      body.appendChild(meta);

      var name = ce('h2', 'tdr-card__name');
      name.textContent = (PRODUCT_NAMES[spec.product_type] || cap(spec.product_type)) + ' stickers';
      body.appendChild(name);

      var summary = ce('p', 'tdr-card__summary');
      summary.textContent = specSummary(spec, product);
      body.appendChild(summary);

      // Action line + its buy group (price + Reorder, pushed right on desktop).
      var controls = ce('div', 'tdr-card__controls');
      var buy = ce('div', 'tdr-card__buy');
      var priceEl = ce('div', 'tdr-card__price');

      var qtyOptions = priceableQtys(spec, config);

      if (!qtyOptions.length) {
        // No priceable quantity for this spec (shouldn't happen for sticker SKUs) —
        // degrade to a "design your stickers" link rather than a broken card.
        var fallback = ce('a', 'td-btn td-btn--dark tdr-card__reorder');
        fallback.href = '/collections/custom-stickers';
        fallback.textContent = 'Reorder →';
        buy.appendChild(priceEl);
        buy.appendChild(fallback);
        controls.appendChild(buy);
        body.appendChild(controls);
        card.appendChild(body);
        return card;
      }

      // Quantity: "Quantity" label + dropdown, inline on the same line.
      var qtyField = ce('label', 'tdr-card__qty');
      var qtyLabel = ce('span', 'tdr-card__qty-label');
      qtyLabel.textContent = 'Quantity';
      qtyField.appendChild(qtyLabel);

      var select = document.createElement('select');
      select.className = 'tdr-card__qty-select';
      qtyOptions.forEach(function (q) {
        var opt = document.createElement('option');
        opt.value = String(q);
        opt.textContent = q + ' stickers';
        select.appendChild(opt);
      });
      select.value = String(defaultQty(spec.qty, qtyOptions));
      qtyField.appendChild(select);
      controls.appendChild(qtyField);

      // Reorder deal for THIS card (config-driven; null/inapplicable → plain list
      // price, no strikethrough the page can't back up). Resolved HERE, before the
      // first thing that reads it — `var` hoisting means an earlier read is silently
      // `undefined` rather than an error, which is how the skip-the-line row went
      // missing with a clean console the first time round.
      var offer = offerConfig(config);
      var offerOn = offerAppliesTo(offer, spec.product_type);

      // ── Skip the line (§1.5) — an inline toggle above the button, NOT a second
      // modal. One upsell interruption per order is the budget; the bump modal spends
      // it. Copy states a POSITION ("front of the print queue"), never a time or a
      // date — A5. Built only when the config block is enabled AND this SKU is covered.
      var skipInput = null;
      if (offerOn && offer.skip) {
        var skipRow = ce('label', 'tdr-card__skip');
        skipInput = document.createElement('input');
        skipInput.type = 'checkbox';
        skipInput.className = 'tdr-card__skip-box';
        var skipText = ce('span', 'tdr-card__skip-txt');
        var skipHead = ce('span', 'tdr-card__skip-head');
        var skipStrong = document.createElement('strong');
        skipStrong.textContent = 'Skip the line and earn ' + offer.skip.bonus + ' more stickers';
        skipHead.appendChild(skipStrong);
        skipHead.appendChild(document.createTextNode(' — ' + formatMoney(offer.skip.price)));
        // Both lines come from config so the wording and the day-counts live in ONE
        // place (A1 lockstep) instead of being retyped into markup. textContent, not
        // innerHTML — no markup in the strings, no injection surface.
        var skipSub = ce('span', 'tdr-card__skip-sub');
        skipSub.textContent = offer.skip.sub;
        skipText.appendChild(skipHead);
        skipText.appendChild(skipSub);
        // The day-count line. "Produced", never "printed": printing is one step of
        // several, and the press finishing is not the order finishing. Both figures are
        // anchored to PROOF APPROVAL — an unanchored count gets counted from checkout,
        // which is how the 24-hour claim turned into a complaint.
        if (offer.skip.note) {
          var skipNote = ce('span', 'tdr-card__skip-note');
          skipNote.textContent = offer.skip.note;
          skipText.appendChild(skipNote);
        }
        skipRow.appendChild(skipInput);
        skipRow.appendChild(skipText);
        body.appendChild(skipRow);
      }
      function skipOn() { return !!(skipInput && skipInput.checked); }

      // Buy group: live price + Reorder CTA.
      var btn = ce('button', 'td-btn td-btn--primary tdr-card__reorder');
      btn.type = 'button';
      btn.textContent = 'Reorder →';
      buy.appendChild(priceEl);
      buy.appendChild(btn);
      controls.appendChild(buy);
      body.appendChild(controls);

      // Bonus row — below the action line (per the mockup), its own row so three
      // stacked price values + a bonus line never crowd the action row. Hidden unless
      // the offer applies. [hidden] is force-hidden in CSS (the flex display would
      // otherwise beat the UA [hidden] rule).
      var bonusEl = ce('p', 'tdr-card__bonus');
      bonusEl.hidden = true;
      body.appendChild(bonusEl);

      var errNote = ce('p', 'tdr-card__err');
      errNote.hidden = true;
      errNote.setAttribute('role', 'status');
      body.appendChild(errNote);

      // What this card charges at the current selection. Single source of truth for the
      // price block, the bump delta and the "total with Skip the Line" line — so all
      // three can never disagree with each other or with the bridge.
      function quoteAt(q) {
        var list = computeStickerPrice(withQty(spec, q), config);
        if (list == null) return null;
        var deal = offerOn ? dealCharge(offer, list, q) : null;
        return { list: list, pay: deal ? deal.pay : r2(list), save: deal ? deal.save : 0 };
      }

      function refreshPrice() {
        var q = Number(select.value);
        var quote = quoteAt(q);
        if (!quote) { priceEl.textContent = ''; setBonus(0, q); return; }
        if (!quote.save) {
          // Graceful degradation — today's exact single-price layout (one big price).
          // Also the honest resting state for bumper / QR / sheets, which the deal
          // deliberately doesn't cover: card and checkout both show list.
          priceEl.textContent = '';
          var only = ce('span', 'tdr-card__price-now');
          only.textContent = formatMoney(quote.pay);
          priceEl.appendChild(only);
          setBonus(bonusFor(q), q);
          return;
        }
        renderPriceBlock(quote.list, quote.pay, quote.save);
        setBonus(bonusFor(q), q);
      }

      // Mirror of the bridge's computeStickerBonus() on the reorder path. The standard
      // ladder is SUPPRESSED here by config — this surface sells on a deep flat price
      // now, so the only stickers in the box beyond the ordered quantity are the ones
      // skip-the-line was paid for.
      function bonusFor(q) {
        if (!offerOn) return 0;
        var base = 0;
        if (offer.bonusRate > 0) base = Math.round(q * offer.bonusRate);
        else if (!offer.suppressLadder) base = ladderBonus(config, spec.product_type, q);
        return base + (skipOn() && offer.skip ? offer.skip.bonus : 0);
      }

      // DOLLARS ONLY, never a percentage: the same rung runs 36%–76% off depending on
      // the spec, so a percentage would invite a comparison between two customers that
      // makes no sense to either of them.
      function renderPriceBlock(list, pay, discount) {
        priceEl.textContent = '';
        var row = ce('span', 'tdr-card__price-row');
        var was = ce('s', 'tdr-card__price-was');
        was.textContent = formatMoney(list);
        var now = ce('span', 'tdr-card__price-now');
        now.textContent = formatMoney(pay);
        row.appendChild(was); row.appendChild(now);
        var save = ce('span', 'tdr-card__save');
        save.textContent = 'You save ' + formatMoney(discount);
        priceEl.appendChild(row); priceEl.appendChild(save);
        // With the add-on ticked the big number is no longer what the customer pays.
        // Show the real total rather than letting checkout be the first place they see it.
        if (skipOn() && offer.skip) {
          var tot = ce('span', 'tdr-card__total');
          tot.textContent = formatMoney(pay + offer.skip.price) + ' total with Skip the Line';
          priceEl.appendChild(tot);
        }
      }

      function setBonus(bonus, q) {
        if (!bonus || bonus <= 0) { bonusEl.hidden = true; bonusEl.textContent = ''; return; }
        bonusEl.innerHTML = GIFT_SVG; // static svg, replaces children
        // Text + bold count live in ONE span (a single flex item beside the icon) so the
        // phrase wraps as a cohesive unit on narrow screens — otherwise "120 in the box"
        // orphans onto its own line with a dangling em dash at the larger font size.
        var txt = ce('span', 'tdr-card__bonus-txt');
        txt.appendChild(document.createTextNode('+' + bonus + ' extra stickers — '));
        var strong = document.createElement('strong');
        strong.textContent = (q + bonus) + ' in the box';
        txt.appendChild(strong);
        bonusEl.appendChild(txt);
        bonusEl.hidden = false;
      }

      function showCardError(msg, withLogin) {
        errNote.textContent = '';
        errNote.appendChild(document.createTextNode(msg));
        if (withLogin) {
          errNote.appendChild(document.createTextNode(' '));
          var a = document.createElement('a');
          a.className = 'tdr-card__err-login';
          a.href = el.login ? el.login.href : (cfg.loginUrl + '?return_to=%2Fpages%2Freorder');
          a.textContent = 'Log in →';
          errNote.appendChild(a);
        }
        errNote.hidden = false;
      }

      select.addEventListener('change', refreshPrice);
      if (skipInput) skipInput.addEventListener('change', refreshPrice);
      refreshPrice();

      // The bump (§1.4) the CURRENT selection qualifies for, or null. The delta is
      // computed here from THIS customer's own spec — 50→100 happens to be a flat
      // $15.00 for everyone (both rungs are flat prices), but 100→200 varies by spec
      // because 200 is rate-derived. Never hardcode either.
      function bumpOffer() {
        // OFF by default (schema `bump_enabled`, default false). The 2026-08-09 deploy
        // was pricing-only; the modal is built and verified but not approved to run, so
        // it stays dark until the setting is ticked in the Customizer. Returning null
        // here means placeOrder() is called directly — the same path as declining.
        if (!cfg.bumpEnabled) return null;
        if (!offerOn) return null;
        var from = Number(select.value);
        var to = BUMP_MAP[from];
        if (!to || qtyOptions.indexOf(to) === -1) return null;
        var a = quoteAt(from), b = quoteAt(to);
        if (!a || !b) return null;
        var delta = r2(b.pay - a.pay);
        if (!(delta > 0)) return null;
        return { from: from, to: to, delta: delta };
      }

      // Place the order at whatever quantity is selected right now.
      function placeOrder() {
        var restore = 'Reorder →';
        btn.disabled = true; // existing single-submit guard — the extra await keeps it held
        btn.textContent = 'Starting your order…';
        function restoreBtn() { btn.disabled = false; btn.textContent = restore; }

        // Re-mint the token (§2a) THEN sign + POST. Never post token-less at full price:
        // an expired session or a failed/absent re-fetch aborts the click with a message
        // rather than silently dropping the customer to list price after showing a deal.
        fetchFreshToken(item.order_id).then(function (res) {
          if (res.state === 'gate') {
            restoreBtn();
            showCardError('Your session expired — log in again to keep your reorder price.', true);
            return;
          }
          if (res.state !== 'ok') {
            restoreBtn();
            showCardError("Couldn't start your order — please refresh and try again.", false);
            return;
          }
          // Fresh valid token → sign + POST. Navigates on success; rejects on bridge error.
          return reorder(spec, Number(select.value), res.token, skipOn()).catch(function (e) {
            restoreBtn();
            showCardError((e && e.message) || 'Could not start your order — please try again.', false);
          });
        }).catch(function (e) {
          restoreBtn();
          showCardError("Couldn't start your order — please refresh and try again.", false);
          if (window.console) console.warn('[td-reorder] reorder click failed', e);
        });
      }

      btn.addEventListener('click', function () {
        errNote.hidden = true;
        errNote.textContent = '';
        // ONE MORE THING (§1.4) — fires once per page, on place-order, then never again.
        // Accepting rewrites the selection so the card behind the modal already shows the
        // new quantity and price when checkout opens.
        var bump = bumpShown ? null : bumpOffer();
        if (bump) {
          bumpShown = true;
          openBump(bump, function (accepted) {
            if (accepted) { select.value = String(bump.to); refreshPrice(); }
            placeOrder();
          });
          return;
        }
        placeOrder();
      });

      card.appendChild(body);
      return card;
    }

    // ── One TAPE design card (2026-10-15) ────────────────────────────────────
    // Quantity only — no length / colour / look editing here (owner decision 8);
    // "Edit design →" opens the PDP at full price. Every number on the card is a row
    // of item.tape.quotes, which the bridge computed with the functions that charge
    // it, so the card and checkout cannot disagree (a rounded per-roll × qty would:
    // 22.19 × 6 = 133.14, the charge is 133.11 — hence totals, never per-roll).
    var TAPE_PDP = { 'custom-water-activated-tape': '/products/custom-water-activated-tape', 'custom-poly-tape': '/products/custom-poly-tape' };

    function renderTapeCard(item) {
      var tape = item.tape;
      var card = ce('div', 'tdr-card tdr-card--tape');

      var thumbWrap = ce('div', 'tdr-card__thumb');
      if (item.thumb_url) {
        var img = document.createElement('img');
        img.className = 'tdr-card__img';
        img.alt = 'Your tape design';
        img.loading = 'lazy';
        img.src = thumbUrl(item.thumb_url);
        img.addEventListener('error', function () {
          thumbWrap.classList.add('tdr-card__thumb--empty');
          if (img.parentNode) img.parentNode.removeChild(img);
        });
        thumbWrap.appendChild(img);
      } else {
        thumbWrap.classList.add('tdr-card__thumb--empty');
      }
      card.appendChild(thumbWrap);

      var body = ce('div', 'tdr-card__body');
      var meta = ce('p', 'tdr-card__meta');
      meta.textContent = [item.order_name, formatDate(item.created_at)].filter(Boolean).join(' · ');
      body.appendChild(meta);
      var name = ce('h2', 'tdr-card__name');
      name.textContent = tape.title;
      body.appendChild(name);
      var summary = ce('p', 'tdr-card__summary');
      summary.textContent = tape.spec_line;
      body.appendChild(summary);

      var controls = ce('div', 'tdr-card__controls');
      var qtyField = ce('label', 'tdr-card__qty');
      var qtyLabel = ce('span', 'tdr-card__qty-label');
      qtyLabel.textContent = 'Rolls';
      qtyField.appendChild(qtyLabel);

      // WAT sells any whole quantity in range: a number input. PolyPro sells its
      // rungs (and its MOQs): the bridge only lists the rungs it will accept.
      var opts = tape.options.map(Number);
      var input;
      if (tape.qty_mode === 'any') {
        input = document.createElement('input');
        input.type = 'number';
        input.inputMode = 'numeric';
        input.className = 'tdr-card__qty-input';
        input.min = String(opts[0]);
        input.max = String(opts[opts.length - 1]);
        input.step = '1';
        input.value = String(tape.default_qty);
        input.setAttribute('aria-label', 'Number of rolls');
      } else {
        input = document.createElement('select');
        input.className = 'tdr-card__qty-select';
        opts.forEach(function (q) {
          var o = document.createElement('option');
          o.value = String(q);
          o.textContent = q + ' rolls';
          input.appendChild(o);
        });
        input.value = String(tape.default_qty);
      }
      qtyField.appendChild(input);
      controls.appendChild(qtyField);

      var buy = ce('div', 'tdr-card__buy');
      var priceEl = ce('div', 'tdr-card__price');
      var btn = ce('button', 'td-btn td-btn--primary tdr-card__reorder');
      btn.type = 'button';
      btn.textContent = 'Reorder →';
      buy.appendChild(priceEl);
      buy.appendChild(btn);
      controls.appendChild(buy);
      body.appendChild(controls);

      var ladder = ce('p', 'tdr-card__ladder');
      ladder.hidden = true;
      body.appendChild(ladder);

      var edit = ce('a', 'tdr-card__edit');
      edit.href = TAPE_PDP[tape.product_type] || '/collections/custom-tape';
      edit.textContent = 'Edit design →';
      body.appendChild(edit);

      var errNote = ce('p', 'tdr-card__err');
      errNote.hidden = true;
      errNote.setAttribute('role', 'status');
      body.appendChild(errNote);

      function currentQty() {
        var q = Number(input.value);
        return Number.isInteger(q) && tape.quotes[q] ? q : null;
      }

      function refresh() {
        var q = currentQty();
        priceEl.textContent = '';
        ladder.hidden = true;
        ladder.textContent = '';
        btn.disabled = q == null;
        if (q == null) {
          var hint = ce('span', 'tdr-card__save');
          hint.textContent = 'Choose ' + opts[0] + '–' + opts[opts.length - 1] + ' rolls';
          priceEl.appendChild(hint);
          return;
        }
        var e = tape.quotes[q];
        if (e.save > 0) {
          var row = ce('span', 'tdr-card__price-row');
          var was = ce('s', 'tdr-card__price-was');
          was.textContent = formatMoney(e.list);
          var now = ce('span', 'tdr-card__price-now');
          now.textContent = formatMoney(e.pays);
          row.appendChild(was); row.appendChild(now);
          var save = ce('span', 'tdr-card__save');
          save.textContent = 'You save ' + formatMoney(e.save);
          priceEl.appendChild(row); priceEl.appendChild(save);
        } else {
          var only = ce('span', 'tdr-card__price-now');
          only.textContent = formatMoney(e.pays);
          priceEl.appendChild(only);
        }
        // The next volume rung they could step up to (skipping 3 — 5% is not worth a
        // line), at its TOTAL with the reward. Hidden when none is left.
        var skip = (tape.skip_rungs || []).map(Number);
        var next = null;
        for (var i = 0; i < tape.rungs.length; i++) {
          var r = Number(tape.rungs[i]);
          if (r > q && skip.indexOf(r) === -1 && tape.quotes[r]) { next = r; break; }
        }
        if (next != null) {
          ladder.textContent = next + ' rolls: ' + formatMoney(tape.quotes[next].pays) + (tape.quotes[next].save > 0 ? ' with your reward' : '');
          ladder.hidden = false;
        }
      }

      function showCardError(msg, withLogin) {
        errNote.textContent = '';
        errNote.appendChild(document.createTextNode(msg));
        if (withLogin) {
          errNote.appendChild(document.createTextNode(' '));
          var a = document.createElement('a');
          a.className = 'tdr-card__err-login';
          a.href = el.login ? el.login.href : (cfg.loginUrl + '?return_to=%2Fpages%2Freorder');
          a.textContent = 'Log in →';
          errNote.appendChild(a);
        }
        errNote.hidden = false;
      }

      input.addEventListener('input', refresh);
      input.addEventListener('change', refresh);
      refresh();

      btn.addEventListener('click', function () {
        var q = currentQty();
        if (q == null) return;
        errNote.hidden = true;
        errNote.textContent = '';
        btn.disabled = true;
        btn.textContent = 'Starting your order…';
        function restoreBtn() { btn.disabled = false; btn.textContent = 'Reorder →'; }
        // Same §2a rule as stickers: a FRESH token right before posting, and never a
        // token-less post after the card showed a reward.
        fetchFreshToken(item.order_id).then(function (res) {
          if (res.state === 'gate') { restoreBtn(); showCardError('Your session expired — log in again to keep your reorder price.', true); return; }
          if (res.state !== 'ok') { restoreBtn(); showCardError("Couldn't start your order — please refresh and try again.", false); return; }
          return reorderTape(tape, q, res.token).catch(function (e) {
            restoreBtn();
            showCardError((e && e.message) || 'Could not start your order — please try again.', false);
          });
        }).catch(function (e) {
          restoreBtn();
          showCardError("Couldn't start your order — please refresh and try again.", false);
          if (window.console) console.warn('[td-reorder] tape reorder click failed', e);
        });
      });

      card.appendChild(body);
      return card;
    }

    // Tape reorder → signed bridge POST. The spec is the bridge's own post_spec (the
    // saved design, already normalized — GP240 full colour on WAT) with only the
    // quantity set here. Same signing scheme and key order as reorder() below.
    function reorderTape(tape, qty, reorderToken) {
      var spec = {};
      for (var k in tape.post_spec) if (Object.prototype.hasOwnProperty.call(tape.post_spec, k)) spec[k] = tape.post_spec[k];
      spec.qty = qty;
      if (!spec.artwork_url) return Promise.reject(new Error('This design has no saved artwork — please start a new order.'));
      var payload = { product_type: tape.product_type, variant_id: cfg.variantId, quantity: 1, spec: spec };
      if (reorderToken) payload.reorder_token = reorderToken;
      return computeHmacHex(payload, cfg.nonce).then(function (signature) {
        var body = { product_type: payload.product_type, variant_id: payload.variant_id, quantity: payload.quantity,
          spec: payload.spec, nonce: cfg.nonce, signature: signature };
        if (reorderToken) body.reorder_token = reorderToken;
        return fetch(cfg.checkoutEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      }).then(function (res) {
        return res.json().then(function (data) { return { res: res, data: data }; });
      }).then(function (out) {
        if (!out.res.ok || !out.data.invoiceUrl) {
          throw new Error(out.data.error || ('Could not start checkout (HTTP ' + out.res.status + ')'));
        }
        window.location.assign(out.data.invoiceUrl);
      });
    }

    // ── Reorder → signed bridge POST → checkout ────────────────────────────────
    // reorderToken is the FRESH token from fetchFreshToken() (§2a), never the one
    // stashed at page load. It rides both the signed payload and the POST body so a
    // MITM can't strip it to silently charge full price; the bridge derives the deal
    // price from it server-side (A1 — the client never sends a price).
    function reorder(spec, qty, reorderToken, skip) {
      // REGULAR sticker spec: never carry offer_promo / sample flags / custom_qty_mode
      // — a reorder is a clean sticker order priced by computeStickerPrice, with the
      // deal applied server-side off the token.
      var postSpec = {
        product_type: spec.product_type,
        size: spec.size,
        shape: spec.shape,
        cut_type: spec.cut_type,
        material: spec.material,
        border_type: spec.border_type,
        qty: qty,
        artwork_url: spec.artwork_url,
        artwork_public_id: spec.artwork_public_id
      };
      if (spec.size === 'custom_size' && spec.custom_size) postSpec.custom_size = spec.custom_size;
      if (spec.cut) postSpec.cut = spec.cut;
      if (spec.cut_complexity) postSpec.cut_complexity = spec.cut_complexity;
      if (spec.position) postSpec.position = spec.position;
      if (spec.white_option) postSpec.white_option = spec.white_option;
      // Rides INSIDE spec, so the request signature already covers it — a MITM can't
      // add the add-on, and the bridge resolves its price + bonus from config anyway
      // (the client never sends either). Omitted entirely when unticked, so an
      // ordinary reorder's signed payload is byte-identical to today's.
      if (skip) postSpec.skip_the_line = true;

      if (!postSpec.artwork_url) {
        return Promise.reject(new Error('This design has no saved artwork — please start a new order.'));
      }

      // Key order MUST match the server's payloadForSig: product_type, variant_id,
      // quantity, spec, [reorder_token] (reorder_token appended last, mirroring the
      // bridge's `if (reorder_token) payloadForSig.reorder_token = ...`).
      var payload = { product_type: postSpec.product_type, variant_id: cfg.variantId, quantity: 1, spec: postSpec };
      if (reorderToken) payload.reorder_token = reorderToken;
      return computeHmacHex(payload, cfg.nonce).then(function (signature) {
        var body = {
          product_type: payload.product_type,
          variant_id: payload.variant_id,
          quantity: payload.quantity,
          spec: payload.spec,
          nonce: cfg.nonce,
          signature: signature
        };
        if (reorderToken) body.reorder_token = reorderToken;
        return fetch(cfg.checkoutEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }).then(function (res) {
        return res.json().then(function (data) { return { res: res, data: data }; });
      }).then(function (out) {
        if (!out.res.ok || !out.data.invoiceUrl) {
          throw new Error(out.data.error || ('Could not start checkout (HTTP ' + out.res.status + ')'));
        }
        window.location.assign(out.data.invoiceUrl);
      });
    }

    load();
  }

  // ── Pricing (ported from the bridge's computeStickerPrice; tier path only —
  //    MVP uses standard quantity tiers, no custom-qty interpolation) ───────────

  // price/sticker = a + b x area, per rung. Byte-for-byte the bridge's customSizeRow()
  // in api/_lib/sticker-price.js — keep the two in lockstep or the card quotes a price
  // the server won't honour. Constants live in the shared config (A1 lockstep).
  function customSizeRow(csCfg, w, h) {
    var ladder = csCfg && csCfg.area_ladder;
    if (!ladder) return null;
    var area = w * h;
    var sur = csCfg.surcharge_multiplier || 1.0;
    var row = {};
    var keys = Object.keys(ladder);
    for (var i = 0; i < keys.length; i++) {
      var q = keys[i];
      var a = ladder[q].a, b = ladder[q].b;
      if (typeof a !== 'number' || typeof b !== 'number') return null;
      row[q] = (a + b * area) * Number(q) * sur;
    }
    return row;
  }

  function computeStickerPrice(spec, config) {
    var product = config.products[spec.product_type];
    if (!product) return null;
    var matrix = config.pricing_matrices[product.pricing_matrix];
    if (!matrix) return null;

    // Custom size → a SYNTHETIC ladder row (2026-08-28), then the identical lookup as
    // a standard size. PORTED VERBATIM from the bridge's customSizeRow() in
    // api/_lib/sticker-price.js; the card computes LIST independently and the deal is
    // min(flat, list × 0.9), so a stale formula here would quote a deal the bridge
    // will not charge. Under the old area-proportional formula a 1"×1" 50-pack listed
    // at $9.07 and the card quoted $8.16 against a server charge of $29.99.
    var total;
    if (spec.size === 'custom_size') {
      var csCfg = config.custom_size_config;
      if (!csCfg || !spec.custom_size) return null;
      var w = spec.custom_size.w, h = spec.custom_size.h;
      if (typeof w !== 'number' || typeof h !== 'number') return null;
      if (csCfg.requires_pricing_matrix
          && product.pricing_matrix !== csCfg.requires_pricing_matrix) return null;
      var customRow = customSizeRow(csCfg, w, h);
      if (!customRow) return null;
      total = customRow[String(spec.qty)];
      if (total == null) return null;
    } else {
      var sizeRow = matrix[spec.size];
      if (!sizeRow) return null;
      total = sizeRow[String(spec.qty)];
      if (total == null) return null;
    }

    total *= (product.substrate_multiplier || 1.0);
    if (product.shape_modifiers && spec.shape) total *= (product.shape_modifiers[spec.shape] || 1.0);
    if (product.cut_type_modifiers && spec.cut_type) total *= (product.cut_type_modifiers[spec.cut_type] || 1.0);
    if (product.cut_modifiers && spec.cut) total *= (product.cut_modifiers[spec.cut] || 1.0);
    if (product.position_modifiers && spec.position) total *= (product.position_modifiers[spec.position] || 1.0);
    if (product.material_modifiers && spec.material) total *= (product.material_modifiers[spec.material] || 1.0);
    if (product.white_options && spec.white_option) total *= (product.white_options[spec.white_option] || 1.0);
    if (product.cut_complexity_modifiers && spec.cut_complexity) total *= (product.cut_complexity_modifiers[spec.cut_complexity] || 1.0);
    return total;
  }

  // Quantities that actually price for this spec (the standard tiers present in the
  // matrix row). Guarantees every offered qty computes and matches the server.
  function priceableQtys(spec, config) {
    var product = config.products[spec.product_type] || {};
    var qtys = (product.available_quantities || []).slice();
    return qtys.filter(function (q) {
      return computeStickerPrice(withQty(spec, q), config) != null;
    });
  }

  // Standard bonus_quantity ladder — only reachable if the reorder deal's
  // suppress_bonus_ladder is ever turned off. Mirrors the bridge's ladder().
  function ladderBonus(config, productType, qty) {
    var bq = config && config.bonus_quantity;
    if (!bq || !bq.tiers || !bq.applies_to) return 0;
    if (bq.applies_to.indexOf(productType) === -1) return 0;
    var rate = bq.tiers[String(qty)];
    return rate ? Math.round(qty * rate) : 0;
  }

  function withQty(spec, q) {
    var out = {};
    for (var k in spec) if (Object.prototype.hasOwnProperty.call(spec, k)) out[k] = spec[k];
    out.qty = q;
    return out;
  }

  function defaultQty(originalQty, options) {
    // Reorder default is 100 (owner) — nudge small originals (10-count samples, 50s) up
    // to a 100 floor, but NEVER downgrade a larger original: a 200/300 reorder still
    // defaults to its own quantity. Falls back to the nearest priceable tier ≥ target,
    // else the largest available, if 100 isn't priceable for this spec.
    var target = Math.max(Number(originalQty) || 0, 100);
    if (options.indexOf(target) !== -1) return target;
    for (var i = 0; i < options.length; i++) if (options[i] >= target) return options[i];
    return options[options.length - 1];
  }

  // ── Spec summary + formatting ──────────────────────────────────────────────
  function specSummary(spec, product) {
    var parts = [];
    if (spec.size === 'custom_size' && spec.custom_size) {
      parts.push(spec.custom_size.w + '″ × ' + spec.custom_size.h + '″');
    } else if (product.size_labels && product.size_labels[spec.size]) {
      parts.push(product.size_labels[spec.size]);
    }
    if (product.shape_labels && product.shape_labels[spec.shape]) parts.push(product.shape_labels[spec.shape]);
    if (spec.material) parts.push(cleanLabel(product.material_labels && product.material_labels[spec.material], spec.material));
    if (product.cut_type_labels && product.cut_type_labels[spec.cut_type]) parts.push(product.cut_type_labels[spec.cut_type]);
    return parts.join(' · ');
  }

  // Strip a trailing " +N%" modifier note from a config label (e.g. "Gloss +5%" → "Gloss").
  function cleanLabel(label, fallback) {
    if (!label) return cap(fallback);
    return String(label).replace(/\s*\+\d+%$/, '');
  }

  function thumbUrl(url) {
    // Small, browser-safe Cloudinary render for the card thumbnail.
    if (typeof url === 'string' && url.indexOf('/upload/') !== -1 && url.indexOf('res.cloudinary.com') !== -1) {
      return url.replace('/upload/', '/upload/f_auto,q_auto,w_240,c_fit/');
    }
    return url;
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try { return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch (e) { return ''; }
  }

  function formatMoney(n) {
    return '$' + (Math.round(n * 100) / 100).toFixed(2);
  }

  function cap(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }

  function ce(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }

  // ── HMAC signing (identical to the builders' computeHmacHex) ────────────────
  function computeHmacHex(payload, nonceHex) {
    var enc = new TextEncoder();
    return crypto.subtle.importKey('raw', enc.encode(nonceHex), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
      .then(function (key) { return crypto.subtle.sign('HMAC', key, enc.encode(JSON.stringify(payload))); })
      .then(function (sig) {
        return Array.from(new Uint8Array(sig)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { if (e && e.target) initAll(e.target); });
})();
